import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from './api';
import type { User, LoginCredentials, RegisterData } from './types';
import axios from 'axios';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<User>;
  register: (data: RegisterData) => Promise<User>;
  logout: () => void;
  loadUser: () => Promise<void>;
  clearError: () => void;
}

/**
 * Traduce errores HTTP a mensajes amigables en español
 */
function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const message = error.response?.data?.message;

    // Si el backend envía un mensaje, usarlo
    if (message && typeof message === 'string') {
      return message;
    }

    // Traducir códigos de estado comunes
    switch (status) {
      case 401:
        return 'Email o contraseña incorrectos';
      case 400:
        return 'Los datos ingresados no son válidos';
      case 409:
        return 'Este email ya está registrado';
      case 500:
        return 'Error en el servidor. Por favor, intenta más tarde';
      case 503:
        return 'El servicio no está disponible. Intenta más tarde';
      default:
        return 'Error de conexión. Verifica tu internet';
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Ha ocurrido un error inesperado';
}

/**
 * Store de autenticación con Zustand
 *
 * @remarks
 * Persiste el token en localStorage para mantener la sesión.
 * El user se recarga automáticamente al iniciar la app.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      /**
       * Login de usuario
       * @returns Usuario autenticado
       */
      login: async (credentials: LoginCredentials): Promise<User> => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login(credentials);
          set({
            user: response.user,
            token: response.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
          return response.user;
        } catch (error) {
          set({
            error: getErrorMessage(error),
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * Registro de nuevo usuario
       * @returns Usuario registrado
       */
      register: async (data: RegisterData): Promise<User> => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.register(data);
          set({
            user: response.user,
            token: response.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
          return response.user;
        } catch (error) {
          set({
            error: getErrorMessage(error),
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * Logout de usuario
       */
      logout: (): void => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
      },

      /**
       * Cargar datos del usuario actual
       */
      loadUser: async (): Promise<void> => {
        const { token } = get();
        if (!token) {
          console.log('[AUTH-DEBUG] loadUser: no hay token, abortando');
          return;
        }

        // [TEMP] Decodificar payload para ver expiración
        try {
          const payloadBase64 = token.split('.')[1];
          const payload = JSON.parse(atob(payloadBase64));
          const now = Date.now() / 1000;
          const expired = payload.exp < now;
          const expiresInHours = ((payload.exp - now) / 3600).toFixed(2);

          console.log('[AUTH-DEBUG] loadUser: token info', {
            sub: payload.sub,
            email: payload.email,
            tenantId: payload.tenantId,
            exp: new Date(payload.exp * 1000).toISOString(),
            expired,
            expiresIn: expired ? 'EXPIRADO' : `${expiresInHours}h`,
          });
        } catch (e) {
          console.error('[AUTH-DEBUG] Error decodificando token:', e);
        }

        set({ isLoading: true });
        try {
          console.log('[AUTH-DEBUG] Llamando authApi.me()...');
          const user = await authApi.me(token);
          console.log('[AUTH-DEBUG] ✅ authApi.me() exitoso, usuario recibido:', {
            id: user.id,
            email: user.email,
          });
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          // Si /auth/me falla, el token es inválido → limpiar estado
          console.error('[AUTH-DEBUG] ❌ authApi.me() falló:', {
            status: axios.isAxiosError(error) ? error.response?.status : 'N/A',
            message: axios.isAxiosError(error) ? error.response?.data?.message : error,
            error,
          });
          console.log('[AUTH-DEBUG] Limpiando sesión completa (user, token, isAuthenticated)');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      /**
       * Limpiar error
       */
      clearError: (): void => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        token: state.token,
      }),
    }
  )
);
